/**
 * One-shot startup migration that stamps the linked host owner's userId onto
 * every persisted record that still carries `ownerUserId === null`.
 *
 * Context: pre-multi-tenant records (agents, loops, schedules, quests, chat
 * rooms) were written with `ownerUserId: null` because the daemon had no
 * concept of an owner. After the single-tenant fallback was removed (every
 * ACL gate flipped to deny-by-default for null owners), those records would
 * become invisible to everyone. This migration brings them into the new
 * model by stamping them with the daemon's linked account userId — the only
 * party that had legitimate access while the daemon was unlinked.
 *
 * Push subscriptions are different: a legacy entry with `userId: null` is a
 * device whose original owning user is unknown. Stamping them to the host
 * owner would silently exfiltrate notifications to the wrong human, so they
 * are deleted instead. The user must re-subscribe from a session the daemon
 * can resolve.
 *
 * Idempotent — the marker file at
 * `$APPOSTLE_HOME/migrations/stamp-legacy-owners.done` makes this a no-op on
 * subsequent boots. The marker is only written after every write succeeds,
 * so a crash mid-migration leaves the marker absent and the migration retries.
 */

import { promises as fs } from "node:fs";
import { existsSync, readFileSync, writeFileSync, mkdirSync } from "node:fs";
import path from "node:path";
import type { Logger } from "pino";

const MARKER_RELATIVE_PATH = path.join("migrations", "stamp-legacy-owners.done");

interface StampLegacyOwnerIdsOptions {
  appostleHome: string;
  ownerUserId: string;
  logger: Logger;
}

interface MigrationSummary {
  agents: number;
  loops: number;
  schedules: number;
  quests: number;
  chatRooms: number;
  pushTokensDeleted: number;
  webPushSubsDeleted: number;
}

export async function stampLegacyOwnerIds(
  options: StampLegacyOwnerIdsOptions,
): Promise<MigrationSummary | null> {
  const { appostleHome, ownerUserId, logger } = options;
  const markerPath = path.join(appostleHome, MARKER_RELATIVE_PATH);
  if (existsSync(markerPath)) {
    return null;
  }

  const log = logger.child({ migration: "stamp-legacy-owners" });
  log.info({ ownerUserId }, "running one-time legacy-owner stamp migration");

  const summary: MigrationSummary = {
    agents: 0,
    loops: 0,
    schedules: 0,
    quests: 0,
    chatRooms: 0,
    pushTokensDeleted: 0,
    webPushSubsDeleted: 0,
  };

  summary.agents = await stampAgents(appostleHome, ownerUserId, log);
  summary.loops = await stampLoops(appostleHome, ownerUserId, log);
  summary.schedules = await stampSchedules(appostleHome, ownerUserId, log);
  summary.quests = await stampQuests(appostleHome, ownerUserId, log);
  summary.chatRooms = await stampChatRooms(appostleHome, ownerUserId, log);
  summary.pushTokensDeleted = pruneLegacyPushTokens(appostleHome, log);
  summary.webPushSubsDeleted = pruneLegacyWebPushSubs(appostleHome, log);

  log.info(summary, "legacy-owner stamp migration complete");

  // Write marker last so a crash mid-migration retries on next boot.
  mkdirSync(path.dirname(markerPath), { recursive: true });
  writeFileSync(
    markerPath,
    JSON.stringify(
      {
        completedAt: new Date().toISOString(),
        ownerUserId,
        summary,
      },
      null,
      2,
    ),
    "utf8",
  );

  return summary;
}

async function readJsonOrNull(filePath: string): Promise<unknown> {
  try {
    const raw = await fs.readFile(filePath, "utf8");
    return JSON.parse(raw);
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code === "ENOENT") return null;
    throw err;
  }
}

async function writeJsonAtomic(filePath: string, value: unknown): Promise<void> {
  await fs.mkdir(path.dirname(filePath), { recursive: true });
  const tmp = `${filePath}.tmp-${process.pid}-${Date.now()}`;
  await fs.writeFile(tmp, JSON.stringify(value, null, 2), "utf8");
  await fs.rename(tmp, filePath);
}

async function stampAgents(home: string, ownerUserId: string, log: Logger): Promise<number> {
  const baseDir = path.join(home, "agents");
  if (!existsSync(baseDir)) return 0;
  let count = 0;
  const projectDirs = await fs.readdir(baseDir, { withFileTypes: true });
  for (const entry of projectDirs) {
    if (!entry.isDirectory()) continue;
    const projectDir = path.join(baseDir, entry.name);
    const files = await fs.readdir(projectDir, { withFileTypes: true });
    for (const file of files) {
      if (!file.isFile() || !file.name.endsWith(".json")) continue;
      const filePath = path.join(projectDir, file.name);
      const record = (await readJsonOrNull(filePath)) as { ownerUserId?: string | null } | null;
      if (!record) continue;
      if (record.ownerUserId == null) {
        record.ownerUserId = ownerUserId;
        await writeJsonAtomic(filePath, record);
        count += 1;
      }
    }
  }
  if (count > 0) log.info({ count }, "stamped legacy agent records");
  return count;
}

async function stampLoops(home: string, ownerUserId: string, log: Logger): Promise<number> {
  const filePath = path.join(home, "loops", "loops.json");
  const raw = (await readJsonOrNull(filePath)) as Array<{ ownerUserId?: string | null }> | null;
  if (!raw || !Array.isArray(raw)) return 0;
  let count = 0;
  for (const loop of raw) {
    if (loop.ownerUserId == null) {
      loop.ownerUserId = ownerUserId;
      count += 1;
    }
  }
  if (count > 0) {
    await writeJsonAtomic(filePath, raw);
    log.info({ count }, "stamped legacy loop records");
  }
  return count;
}

async function stampSchedules(home: string, ownerUserId: string, log: Logger): Promise<number> {
  const dir = path.join(home, "schedules");
  if (!existsSync(dir)) return 0;
  let count = 0;
  const entries = await fs.readdir(dir, { withFileTypes: true });
  for (const entry of entries) {
    if (!entry.isFile() || !entry.name.endsWith(".json")) continue;
    const filePath = path.join(dir, entry.name);
    const record = (await readJsonOrNull(filePath)) as { ownerUserId?: string | null } | null;
    if (!record) continue;
    if (record.ownerUserId == null) {
      record.ownerUserId = ownerUserId;
      await writeJsonAtomic(filePath, record);
      count += 1;
    }
  }
  if (count > 0) log.info({ count }, "stamped legacy schedule records");
  return count;
}

async function stampQuests(home: string, ownerUserId: string, log: Logger): Promise<number> {
  const filePath = path.join(home, "quests", "quests.json");
  const raw = (await readJsonOrNull(filePath)) as Array<{ ownerUserId?: string | null }> | null;
  if (!raw || !Array.isArray(raw)) return 0;
  let count = 0;
  for (const quest of raw) {
    if (quest.ownerUserId == null) {
      quest.ownerUserId = ownerUserId;
      count += 1;
    }
  }
  if (count > 0) {
    await writeJsonAtomic(filePath, raw);
    log.info({ count }, "stamped legacy quest records");
  }
  return count;
}

async function stampChatRooms(home: string, ownerUserId: string, log: Logger): Promise<number> {
  const filePath = path.join(home, "chat", "rooms.json");
  const raw = (await readJsonOrNull(filePath)) as {
    rooms?: Array<{ ownerUserId?: string | null }>;
  } | null;
  if (!raw || !Array.isArray(raw.rooms)) return 0;
  let count = 0;
  for (const room of raw.rooms) {
    if (room.ownerUserId == null) {
      room.ownerUserId = ownerUserId;
      count += 1;
    }
  }
  if (count > 0) {
    await writeJsonAtomic(filePath, raw);
    log.info({ count }, "stamped legacy chat-room records");
  }
  return count;
}

interface PushTokenEntryShape {
  token?: string;
  deviceId?: string;
  userId?: string | null;
}

interface PushTokenFileShape {
  entries?: PushTokenEntryShape[];
  /** Legacy v0 — bare token strings, no userId. All treated as null-owner. */
  tokens?: string[];
}

function pruneLegacyPushTokens(home: string, log: Logger): number {
  const filePath = path.join(home, "push-tokens.json");
  if (!existsSync(filePath)) return 0;
  let parsed: PushTokenFileShape;
  try {
    parsed = JSON.parse(readFileSync(filePath, "utf8")) as PushTokenFileShape;
  } catch {
    return 0;
  }
  if (Array.isArray(parsed.entries)) {
    const before = parsed.entries.length;
    parsed.entries = parsed.entries.filter(
      (e) => typeof e.userId === "string" && e.userId.length > 0,
    );
    const removed = before - parsed.entries.length;
    if (removed > 0) {
      writeFileSync(filePath, JSON.stringify(parsed, null, 2), "utf8");
      log.warn({ removed }, "dropped legacy push-token entries with null userId");
    }
    return removed;
  }
  if (Array.isArray(parsed.tokens)) {
    const removed = parsed.tokens.length;
    if (removed > 0) {
      writeFileSync(filePath, JSON.stringify({ entries: [] }, null, 2), "utf8");
      log.warn({ removed }, "dropped legacy v0 push-token list (no userId field)");
    }
    return removed;
  }
  return 0;
}

interface WebPushEntryShape {
  subscription?: unknown;
  deviceId?: string;
  userId?: string | null;
  updatedAt?: string;
}

interface WebPushFileShape {
  vapidKey?: string;
  entries?: WebPushEntryShape[];
  /** Legacy v0 — bare subscriptions, no userId. All treated as null-owner. */
  subs?: unknown[];
}

function pruneLegacyWebPushSubs(home: string, log: Logger): number {
  const filePath = path.join(home, "web-push-subs.json");
  if (!existsSync(filePath)) return 0;
  let parsed: WebPushFileShape;
  try {
    parsed = JSON.parse(readFileSync(filePath, "utf8")) as WebPushFileShape;
  } catch {
    return 0;
  }
  let removed = 0;
  if (Array.isArray(parsed.entries)) {
    const before = parsed.entries.length;
    parsed.entries = parsed.entries.filter(
      (e) => typeof e.userId === "string" && e.userId.length > 0,
    );
    removed += before - parsed.entries.length;
  }
  if (Array.isArray(parsed.subs)) {
    removed += parsed.subs.length;
    parsed.subs = [];
  }
  if (removed > 0) {
    writeFileSync(filePath, JSON.stringify(parsed, null, 2), "utf8");
    log.warn({ removed }, "dropped legacy web-push subscriptions with null userId");
  }
  return removed;
}
